import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];

const OPTIONS = [
  "Instagram / Social Media",
  "Google Search",
  "Referral from a friend",
  "LinkedIn",
  "Advertisement",
  "Other",
];

export const WelcomeModal = ({ open, onClose }) => {
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/welcome-source`, { source: selected, detail });
    } catch (e) {
      /* non-blocking */
    }
    setSubmitting(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center px-5"
          data-testid="welcome-modal"
        >
          <motion.div
            className="absolute inset-0 bg-[#1A1713]/70 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative w-full max-w-xl bg-[#FDFBF7] border border-[#E8E4D9] p-8 md:p-12 shadow-2xl"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <div className="flex items-center gap-3 text-[#C5A059]">
              <Logo size={34} />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#78716C]">
                Aurelume Branding
              </span>
            </div>

            <h2 className="font-serif text-4xl md:text-5xl leading-none tracking-tight mt-8">
              Before we begin,
              <br />
              <span className="italic text-[#C5A059]">how did you find us?</span>
            </h2>
            <p className="text-sm text-[#78716C] mt-4 max-w-sm">
              A small question so we can craft a better experience. It takes a
              second.
            </p>

            <div className="grid grid-cols-2 gap-2 mt-8">
              {OPTIONS.map((opt) => (
                <button
                  key={opt}
                  data-testid={`welcome-option-${opt.split(" ")[0].toLowerCase()}`}
                  onClick={() => setSelected(opt)}
                  className={`text-left text-sm px-4 py-3 border transition-colors duration-300 ${
                    selected === opt
                      ? "border-[#C5A059] bg-[#C5A059]/10 text-[#1A1713]"
                      : "border-[#E8E4D9] text-[#78716C] hover:border-[#C5A059]"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>

            <input
              data-testid="welcome-detail-input"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="Anything to add? (optional)"
              className="w-full mt-4 bg-transparent border-b border-[#E8E4D9] py-3 text-sm outline-none focus:border-[#C5A059] transition-colors"
            />

            <div className="flex items-center justify-between mt-8">
              <button
                data-testid="welcome-skip-btn"
                onClick={onClose}
                className="text-xs uppercase tracking-[0.2em] text-[#78716C] link-underline"
              >
                Skip
              </button>
              <button
                data-testid="welcome-submit-btn"
                onClick={submit}
                disabled={!selected || submitting}
                className="group inline-flex items-center gap-3 bg-[#1A1713] text-[#FDFBF7] px-7 py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-40 transition-transform active:scale-95"
              >
                {submitting ? "Entering" : "Enter Site"}
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
